import type jsPDF from 'jspdf';
import type { Primitive, Zeichnung } from '../../components/asplan/skizzen/primitive';
import { farbe } from '../../components/asplan/skizzen/primitive';

/**
 * Der PDF-Renderer der A+S-Plan-Skizzen.
 *
 * Gegenstueck zu `components/asplan/skizzen/SkizzeSvg.tsx`: Beide lesen
 * DIESELBE Primitivenliste, die eine reine Zeichenfunktion erzeugt hat.
 * Deshalb koennen Bildschirm und Druck nicht auseinanderlaufen — was die
 * Zeichnung aussagt (Reihenfolge der Kammern, Richtung des Pfeils, welche
 * Schicht nicht beprobt ist), steht in der Liste und ist dort getestet.
 *
 * Auch dieser Renderer entscheidet nichts. Er uebersetzt Millimeter in
 * Millimeter — jsPDF rechnet ohnehin in mm, deshalb ist die Umrechnung eine
 * blosse Verschiebung um den Ursprung der Zeichnung.
 *
 * ═══ WAS HIER FEHLT UND WARUM ═════════════════════════════════════════════
 * `bild`-Primitive (ISO-7010-Zeichen) werden UEBERSPRUNGEN, nicht gezeichnet.
 * Ein Bild landet in jsPDF nur ueber `addImage`, und das verlangt entweder
 * eine Data-URL oder ein bereits geladenes Element — beides ist von hier aus
 * nicht synchron zu haben. Die Skizzen dieses Moduls kommen ohne Zeichen aus;
 * sie stehen auf den AUSHAENGEN, und die werden ueber den Browser gedruckt,
 * wo das Bild schlicht ein `<img>` ist.
 *
 * Sollte je eine Skizze ein Zeichen brauchen, gilt die dokumentierte Falle:
 * `piktoPfadLokal` verwenden, nicht `piktoPfad` — die Storage-URL „taintet"
 * den Canvas beim Rastern, und der Export bricht ab.
 */

/** Umrechnung: Punktgroesse der Zeichnung → jsPDF-Schriftgroesse. */
const SCHRIFT_FAKTOR = 1;

interface Ablage {
    /** Linke obere Ecke der Zeichnung auf der Seite, in mm. */
    x: number;
    y: number;
    /** Faktor, mit dem die gesamte Zeichnung skaliert wird. */
    massstab?: number;
}

/** Wandelt einen Hex-Farbwert in die drei Kanaele, die jsPDF erwartet. */
const rgb = (hex: string): [number, number, number] => {
    const h = hex.replace('#', '');
    const voll = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
    return [
        parseInt(voll.slice(0, 2), 16) || 0,
        parseInt(voll.slice(2, 4), 16) || 0,
        parseInt(voll.slice(4, 6), 16) || 0,
    ];
};

/**
 * Zeichnet eine `Zeichnung` an eine Stelle des Dokuments.
 *
 * Gibt die Hoehe zurueck, die sie belegt hat — der Aufrufer braucht sie, um
 * den Cursor weiterzusetzen, und soll sie nicht selbst ausrechnen muessen.
 */
export const zeichneNachPdf = (doc: jsPDF, z: Zeichnung, ablage: Ablage): number => {
    const m = ablage.massstab ?? 1;
    const px = (v: number) => ablage.x + v * m;
    const py = (v: number) => ablage.y + v * m;

    for (const t of z.teile) male(doc, t, px, py, m);

    // Zustand zuruecksetzen, damit der folgende Text nicht die letzte
    // Skizzenfarbe erbt. Ohne das faerbt sich der naechste Absatz mit.
    doc.setDrawColor(0, 0, 0);
    doc.setFillColor(0, 0, 0);
    doc.setTextColor(0, 0, 0);
    doc.setLineDashPattern([], 0);
    doc.setLineWidth(0.2);

    return z.hoehe * m;
};

const male = (
    doc: jsPDF,
    t: Primitive,
    px: (v: number) => number,
    py: (v: number) => number,
    m: number,
): void => {
    switch (t.art) {
        case 'rechteck': {
            const hatFuellung = !!t.fuellung && t.fuellung !== 'none';
            if (hatFuellung) {
                const [r, g, b] = rgb(farbe(t.fuellung, '#ffffff'));
                doc.setFillColor(r, g, b);
            }
            const [lr, lg, lb] = rgb(farbe(t.linie, '#0f172a'));
            doc.setDrawColor(lr, lg, lb);
            doc.setLineWidth(0.3 * m);
            doc.setLineDashPattern(t.strich === 'gestrichelt' ? [1.6 * m, 1.2 * m] : [], 0);
            doc.rect(px(t.x), py(t.y), t.b * m, t.h * m, hatFuellung ? 'FD' : 'D');
            doc.setLineDashPattern([], 0);
            break;
        }

        case 'linie': {
            const [r, g, b] = rgb(farbe(t.linie, '#0f172a'));
            doc.setDrawColor(r, g, b);
            doc.setLineWidth((t.staerke ?? 0.3) * m);
            doc.setLineDashPattern(t.strich === 'gestrichelt' ? [1.6 * m, 1.2 * m] : [], 0);
            doc.line(px(t.x1), py(t.y1), px(t.x2), py(t.y2));
            doc.setLineDashPattern([], 0);
            break;
        }

        case 'pfeil': {
            // Spitze als gefuelltes Dreieck — dieselbe Rechnung wie im
            // SVG-Renderer, damit der Pfeil auf Papier so aussieht wie am
            // Bildschirm.
            const dx = t.x2 - t.x1;
            const dy = t.y2 - t.y1;
            const laenge = Math.hypot(dx, dy) || 1;
            const s = t.spitze ?? 3;
            const ex = dx / laenge;
            const ey = dy / laenge;
            const sx = t.x2 - ex * s;
            const sy = t.y2 - ey * s;
            const nx = -ey;
            const ny = ex;

            const [r, g, b] = rgb(farbe(t.linie, '#0f172a'));
            doc.setDrawColor(r, g, b);
            doc.setFillColor(r, g, b);
            doc.setLineWidth((t.staerke ?? 0.4) * m);
            doc.line(px(t.x1), py(t.y1), px(sx), py(sy));
            doc.triangle(
                px(t.x2), py(t.y2),
                px(sx + nx * s * 0.4), py(sy + ny * s * 0.4),
                px(sx - nx * s * 0.4), py(sy - ny * s * 0.4),
                'F',
            );
            break;
        }

        case 'text': {
            const [r, g, b] = rgb(farbe(t.farbe, '#0f172a'));
            doc.setTextColor(r, g, b);
            doc.setFont('helvetica', t.fett ? 'bold' : t.kursiv ? 'italic' : 'normal');
            doc.setFontSize(t.groesse * SCHRIFT_FAKTOR * m);
            doc.text(t.text, px(t.x), py(t.y), {
                align: t.anker === 'mitte' ? 'center' : t.anker === 'rechts' ? 'right' : 'left',
            });
            break;
        }

        case 'masslinie': {
            const [r, g, b] = rgb('#64748b');
            doc.setDrawColor(r, g, b);
            doc.setTextColor(r, g, b);
            doc.setLineWidth(0.25 * m);

            const waagerecht = Math.abs(t.y2 - t.y1) < 0.01;
            doc.line(px(t.x1), py(t.y1), px(t.x2), py(t.y2));
            // Begrenzungsstriche
            if (waagerecht) {
                doc.line(px(t.x1), py(t.y1 - 1.2), px(t.x1), py(t.y1 + 1.2));
                doc.line(px(t.x2), py(t.y2 - 1.2), px(t.x2), py(t.y2 + 1.2));
            } else {
                doc.line(px(t.x1 - 1.2), py(t.y1), px(t.x1 + 1.2), py(t.y1));
                doc.line(px(t.x2 - 1.2), py(t.y2), px(t.x2 + 1.2), py(t.y2));
            }

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(6 * m);
            const mx = (t.x1 + t.x2) / 2;
            const my = (t.y1 + t.y2) / 2;
            doc.text(
                t.text,
                px(waagerecht ? mx : mx + 2),
                py(waagerecht ? my - 1.4 : my),
                { align: waagerecht ? 'center' : 'left' },
            );
            break;
        }

        case 'bild':
            // Siehe Dateikopf: bewusst uebersprungen.
            break;

        default:
            break;
    }
};

/**
 * Die Hoehe, die eine Zeichnung bei gegebener Breite belegt.
 *
 * Damit kann der Generator VOR dem Zeichnen entscheiden, ob sie noch auf die
 * Seite passt — eine halb gezeichnete Skizze am Seitenende laesst sich nicht
 * mehr zurueckholen.
 */
export const skizzenHoehe = (z: Zeichnung, verfuegbareBreite: number): number => {
    const massstab = Math.min(1, verfuegbareBreite / z.breite);
    return z.hoehe * massstab;
};

/** Der Massstab, mit dem eine Zeichnung in die verfuegbare Breite passt. */
export const skizzenMassstab = (z: Zeichnung, verfuegbareBreite: number): number =>
    Math.min(1, verfuegbareBreite / z.breite);
